import DOMPurify from 'dompurify'
import { ArrowLeft, FileText, X } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { toast } from 'sonner'
import { useUserRol, useTienePermiso } from '../App'
import { actualizarAceptado, guardarPrueba } from '../services/pruebasService'
import { convertirWordANotaHtml } from '../lib/wordToHtml'
import { RechazoModal } from "./RechazoModal"
import { Button } from "./ui/button"
import { Card, CardContent } from "./ui/card"
import { Input } from "./ui/input"
import { Switch } from "./ui/switch"
import { Textarea } from "./ui/textarea"

interface FormDataAdicional {
  ROW_ID?: string
  nombre_prueba: string
  fecha: string
  nota_doc: string
  observaciones: string
  mostrarObservaciones: boolean
  realizo: string
  superviso: string
}

interface Props {
  mode: "create" | "view" | "edit"
  initialData?: any
  onSave?: (data: FormDataAdicional) => void
  onCancel?: () => void
  realizoUser?: string
  onRefreshBackend?: () => void
  rol?: string
  pedidoRowId?: string
  onAutosave?: (result: { rowId: string; nomId: string }) => void
}

const DEFAULTS: FormDataAdicional = {
  nombre_prueba: '', fecha: '', nota_doc: '', observaciones: '', mostrarObservaciones: false, realizo: '', superviso: '',
}

export function PruebaAdicionalForm({ mode, initialData = {}, onSave, onCancel, realizoUser, onRefreshBackend, rol, pedidoRowId, onAutosave }: Props) {
  const globalRol = useUserRol()
  const puedeRevisar = useTienePermiso('aprobar_rechazar_resultado');
  const effectiveRol = rol || globalRol
  const [formData, setFormData] = useState<FormDataAdicional>({
    ...DEFAULTS,
    ...initialData,
    realizo: mode === 'create' && realizoUser ? realizoUser : (initialData?.realizo || ''),
    mostrarObservaciones: initialData?.MOSTRAR_OBSERVACIONES === true,
  })
  const [editMode, setEditMode] = useState(false)
  const [showRechazoModal, setShowRechazoModal] = useState(false)
  const [rechazoLoading, setRechazoLoading] = useState(false)

  const initialDataSyncedRef = useRef(false)
  useEffect(() => {
    if (initialDataSyncedRef.current) return
    initialDataSyncedRef.current = true
    setFormData({
      ...DEFAULTS,
      ...initialData,
      realizo: mode === 'create' && realizoUser ? realizoUser : (initialData?.realizo || ''),
      mostrarObservaciones: initialData?.MOSTRAR_OBSERVACIONES === true,
    })
    setEditMode(false)
  }, [initialData])

  const aceptadoRaw = initialData?.ACEPTADO ?? initialData?.aceptado
  const aceptadoValue = typeof aceptadoRaw === 'string' ? aceptadoRaw.toLowerCase().trim() : ''
  const isAceptado = aceptadoValue === 'aceptado' || aceptadoValue === 'rechazado'
  const isReadOnly = mode === 'view' && !editMode
  const [autosaveRowId, setAutosaveRowId] = useState<string>(formData?.ROW_ID || initialData?.ROW_ID || '')
  const [autosaveNomId, setAutosaveNomId] = useState<string>(initialData?.nomId || '')
  const rowId = autosaveRowId
  const nomId = autosaveNomId

  const set = (field: keyof FormDataAdicional, value: any) =>
    setFormData(prev => ({ ...prev, [field]: value }))

  const formPayload = () => {
    const { ROW_ID, ...rest } = formData
    return rest
  }

  const [guardando, setGuardando] = useState(false)

  const handleSave = async () => {
    if (guardando) return
    if (!formData.nombre_prueba.trim()) {
      toast.error('Captura el nombre de la prueba.')
      return
    }
    setGuardando(true)
    try {
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) {
        await new Promise(r => setTimeout(r, 100))
      }
      if (onSave) onSave(formData)
    } finally {
      setGuardando(false)
    }
  }

  // ── Autosave (borrador) ──────────────────────────────────────────────────
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isFirstAutosaveRenderRef = useRef(true)
  const autosaveInFlightRef = useRef(false)
  const rowIdRef = useRef(rowId)
  const nomIdRef = useRef(nomId)
  useEffect(() => { rowIdRef.current = rowId }, [rowId])
  useEffect(() => { nomIdRef.current = nomId }, [nomId])

  const doAutosave = async () => {
    if (autosaveInFlightRef.current) return
    autosaveInFlightRef.current = true
    try {
      const { pruebaId, nomId: savedNomId } = await guardarPrueba(
        'PRUEBA ADICIONAL',
        pedidoRowId || '',
        formPayload(),
        nomIdRef.current || undefined,
        rowIdRef.current || undefined,
        true,
      )
      setAutosaveRowId(String(pruebaId))
      setAutosaveNomId(savedNomId)
      rowIdRef.current = String(pruebaId)
      nomIdRef.current = savedNomId
      onAutosave?.({ rowId: String(pruebaId), nomId: savedNomId })
    } catch (err) {
      console.warn('Autosave de borrador falló (silencioso):', err)
    } finally {
      autosaveInFlightRef.current = false
    }
  }

  useEffect(() => {
    if (isFirstAutosaveRenderRef.current) {
      isFirstAutosaveRenderRef.current = false
      return
    }
    if (mode === 'view' || isAceptado) return

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null
      doAutosave()
    }, 1500)

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, mode, isAceptado])

  // Si hay un autoguardado pendiente (debounce de 1.5s) y el usuario navega hacia
  // atrás antes de que se dispare, se cancelaba sin guardarse nunca. Al salir,
  // se ejecuta de inmediato ese guardado pendiente con los datos más recientes.
  const handleBackFlush = () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
      autosaveTimerRef.current = null
      if (mode !== 'view' && !isAceptado) {
        doAutosave()
      }
    }
    onCancel?.()
  }

  const handleGuardarEdicion = () => {
    if (!window.confirm('¿Seguro que quieres guardar y aceptar la edición de esta Prueba Adicional? Esta acción no se puede deshacer.')) return
    if (onSave) onSave({ ...formData, ROW_ID: rowId, _EDIT_MODE: true } as any)
  }

  const handleAccion = async () => {
    if (!window.confirm('Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres aceptar esta Prueba Adicional?')) return
    try {
      // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
      // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
      await actualizarAceptado(rowId, 'aceptado', undefined, undefined, nomId || undefined)
      toast.success('Prueba aceptada correctamente.')
      onRefreshBackend?.()
    } catch (e: any) { toast.error(e.message) }
  }

  const inputCls = (extra?: string) =>
    `h-6 text-xs px-1 ${isReadOnly ? 'bg-transparent border-0 shadow-none cursor-not-allowed' : ''} ${extra ?? ''}`

  return (
    <>
      {onCancel && (
        <Button variant="ghost" size="sm" onClick={handleBackFlush}>
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Button>
      )}
      <Card className="w-full max-w-4xl mx-auto">
      <CardContent className="p-4 space-y-3">

        {/* ── HEADER ── */}
        <table className="w-full text-xs border-collapse border-2 border-gray-700">
          <tbody>
            <tr>
              <td className="border border-gray-500 px-1 py-1 text-center font-bold">PRUEBA ADICIONAL</td>
            </tr>
            <tr>
              <td className="border border-gray-500 px-1 py-1">
                <div className="flex items-center gap-2">
                  <span className="font-bold whitespace-nowrap">NOMBRE DE LA PRUEBA:</span>
                  <Input className={inputCls('flex-1')}
                    value={formData.nombre_prueba} readOnly={isReadOnly}
                    placeholder="Ej. Prueba de resistencia a la luz UV"
                    onChange={e => set('nombre_prueba', e.target.value)} />
                </div>
              </td>
            </tr>
            <tr>
              <td className="border border-gray-500 px-1 py-1">
                <div className="flex items-center gap-2">
                  <span className="font-bold whitespace-nowrap">FECHA:</span>
                  <Input type="date" className={inputCls('w-40')}
                    value={formData.fecha} readOnly={isReadOnly}
                    onChange={e => set('fecha', e.target.value)} />
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        {/* ── Documento Word ── */}
        {!isReadOnly && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Documento Word</p>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 cursor-pointer border rounded-lg px-3 py-2 text-sm hover:bg-muted/50 transition-colors">
                <FileText className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Cargar .docx</span>
                <input
                  type="file"
                  accept=".docx"
                  className="hidden"
                  onChange={async e => {
                    const file = e.target.files?.[0]
                    if (!file) return
                    try {
                      const html = await convertirWordANotaHtml(file)
                      set('nota_doc', html)
                      toast.success('Documento cargado correctamente')
                    } catch {
                      toast.error('Error al procesar el archivo Word')
                    }
                    e.target.value = ''
                  }}
                />
              </label>
              {formData.nota_doc && (
                <button
                  type="button"
                  onClick={() => set('nota_doc', '')}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive transition-colors"
                >
                  <X className="w-3 h-3" />
                  Limpiar
                </button>
              )}
            </div>
            {formData.nota_doc && (
              <div
                className="prose prose-sm max-w-none border rounded-lg p-3 bg-muted/10 text-xs max-h-64 overflow-y-auto"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formData.nota_doc) }}
              />
            )}
          </div>
        )}
        {isReadOnly && formData.nota_doc && (
          <div
            className="prose prose-sm max-w-none text-xs"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formData.nota_doc) }}
          />
        )}

        {/* ── Realizó / Supervisó ── */}
        <table className="w-full text-xs border-collapse border border-gray-500">
          <tbody>
            <tr>
              <td className="border border-gray-500 p-2 align-top w-1/2">
                <div className="text-xs font-medium mb-1">Realizó:</div>
                <Input value={formData.realizo} readOnly
                  className="h-6 text-xs border-0 shadow-none bg-transparent cursor-not-allowed" />
              </td>
              <td className="border border-gray-500 p-2 align-top w-1/2">
                <div className="text-xs font-medium mb-1">Supervisó:</div>
                <Input value={formData.superviso} readOnly
                  className="h-6 text-xs border-0 shadow-none bg-transparent cursor-not-allowed" />
              </td>
            </tr>
          </tbody>
        </table>

        {/* ── Observaciones ── */}
        <table className="w-full text-xs border-collapse border border-gray-500">
          <tbody>
            <tr>
              <td className="border border-gray-500 px-2 py-1">
                <div className="font-medium mb-1 flex items-center gap-2">
                  Observaciones:
                  <span className="ml-auto flex items-center gap-1.5 text-xs font-normal text-muted-foreground">
                    Mostrar en Reporte Cliente
                    <Switch checked={formData.mostrarObservaciones === true} disabled={isReadOnly}
                      onCheckedChange={(checked) => set('mostrarObservaciones', checked)} />
                  </span>
                </div>
                <Textarea value={formData.observaciones} readOnly={isReadOnly} rows={2}
                  className={`text-xs ${isReadOnly ? 'border-0 shadow-none bg-transparent resize-none' : ''}`}
                  onChange={e => set('observaciones', e.target.value)} />
              </td>
            </tr>
          </tbody>
        </table>

        <div className="flex gap-2 justify-end">
          {onCancel && <Button variant="outline" onClick={handleBackFlush}>Cancelar</Button>}
          {mode === 'view' && !editMode && !isAceptado && puedeRevisar && (
            <>
              <Button variant="default" onClick={handleAccion}>Aceptar</Button>
              <Button variant="destructive" onClick={() => setShowRechazoModal(true)}>Rechazar</Button>
              <Button variant="secondary" onClick={() => setEditMode(true)}>Editar</Button>
            </>
          )}
          {mode === 'view' && editMode && !isAceptado && puedeRevisar && (
            <Button variant="default" onClick={handleGuardarEdicion}>Guardar Edición</Button>
          )}
          {mode === 'create' && (
            <Button onClick={handleSave} disabled={guardando}>{guardando ? 'Guardando...' : 'Guardar Formulario'}</Button>
          )}
        </div>

      </CardContent>

      <RechazoModal
        open={showRechazoModal}
        onClose={() => setShowRechazoModal(false)}
        loading={rechazoLoading}
        onSubmit={async (razon: string) => {
          setRechazoLoading(true)
          try {
            // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
            // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
            if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
            while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
            await actualizarAceptado(rowId, 'rechazado', razon, undefined, nomId || undefined)
            toast.success('Prueba rechazada')
            setShowRechazoModal(false)
            onRefreshBackend?.()
          } catch (e: any) { toast.error(e.message) }
          finally { setRechazoLoading(false) }
        }}
      />
    </Card>
    </>
  )
}
